import {
  Award, ShieldCheck, BookOpen, Users, Trophy, Star
} from 'lucide-react';

export const CAREER_SECTIONS = [
  {
    id: "certifications",
    title: "Certifications",
    icon: Award,
    category: "career",
    allowMultiple: true,
    defaultVisible: false,
    emptyState: "No certifications added yet.",
    editorComponent: "GenericListEditor",
    rendererComponent: "GenericListRenderer",
    layout: {
      preferredColumn: "main",
      priority: 45,
      collapsible: true,
      allowPageBreak: true,
      keepTogether: false,
    },
    capabilities: {
      aiRewrite: false,
      reorder: true,
      duplicate: true,
      deletable: true,
      hideable: true,
      exportable: true,
    },
    recommendedFor: ["software", "finance", "healthcare"],
    badges: ["Recommended"],
    fields: [
      { name: "name", label: "Certification Name", type: "text", required: true, placeholder: "e.g. AWS Certified Cloud Practitioner" },
      { name: "issuer", label: "Issuer / Authority", type: "text", required: true, placeholder: "e.g. Amazon Web Services" },
      { name: "date", label: "Date Issued", type: "text", required: false, placeholder: "e.g. Oct 2024" }
    ],
    defaultData: { name: "", issuer: "", date: "" }
  },
  {
    id: "licenses",
    title: "Licenses",
    icon: ShieldCheck,
    category: "career",
    allowMultiple: true,
    defaultVisible: false,
    emptyState: "No licenses added yet.",
    editorComponent: "GenericListEditor",
    rendererComponent: "GenericListRenderer",
    layout: {
      preferredColumn: "main",
      priority: 40,
      collapsible: true,
      allowPageBreak: true,
      keepTogether: false,
    },
    capabilities: {
      aiRewrite: false,
      reorder: true,
      duplicate: true,
      deletable: true,
      hideable: true,
      exportable: true,
    },
    recommendedFor: ["healthcare", "engineering"],
    badges: [],
    fields: [
      { name: "name", label: "License Name", type: "text", required: true, placeholder: "e.g. Registered Professional Nurse" },
      { name: "issuer", label: "State / Board", type: "text", required: true, placeholder: "e.g. California Board of Nursing" },
      { name: "date", label: "Expiry / Date", type: "text", required: false, placeholder: "e.g. Expires Dec 2026" }
    ],
    defaultData: { name: "", issuer: "", date: "" }
  },
  {
    id: "courses",
    title: "Courses",
    icon: BookOpen,
    category: "career",
    allowMultiple: true,
    defaultVisible: false,
    emptyState: "No courses added yet.",
    editorComponent: "GenericListEditor",
    rendererComponent: "GenericListRenderer",
    layout: {
      preferredColumn: "main",
      priority: 35,
      collapsible: true,
      allowPageBreak: true,
      keepTogether: false,
    },
    capabilities: {
      aiRewrite: false,
      reorder: true,
      duplicate: true,
      deletable: true,
      hideable: true,
      exportable: true,
    },
    recommendedFor: ["student"],
    badges: [],
    fields: [
      { name: "name", label: "Course Title", type: "text", required: true, placeholder: "e.g. Advanced Machine Learning" },
      { name: "platform", label: "Platform / School", type: "text", required: true, placeholder: "e.g. Coursera / Stanford" },
      { name: "date", label: "Date Completed", type: "text", required: false, placeholder: "e.g. Aug 2024" }
    ],
    defaultData: { name: "", platform: "", date: "" }
  },
  {
    id: "memberships",
    title: "Professional Memberships",
    icon: Users,
    category: "career",
    allowMultiple: true,
    defaultVisible: false,
    emptyState: "No professional memberships added yet.",
    editorComponent: "GenericListEditor",
    rendererComponent: "GenericListRenderer",
    layout: {
      preferredColumn: "main",
      priority: 30,
      collapsible: true,
      allowPageBreak: true,
      keepTogether: false,
    },
    capabilities: {
      aiRewrite: false,
      reorder: true,
      duplicate: true,
      deletable: true,
      hideable: true,
      exportable: true,
    },
    recommendedFor: ["research", "academic"],
    badges: [],
    fields: [
      { name: "role", label: "Role / Rank", type: "text", required: true, placeholder: "e.g. Senior Member" },
      { name: "organization", label: "Organization Name", type: "text", required: true, placeholder: "e.g. IEEE Computer Society" },
      { name: "duration", label: "Duration / Dates", type: "text", required: false, placeholder: "e.g. 2021 - Present" }
    ],
    defaultData: { role: "", organization: "", duration: "" }
  },
  {
    id: "awards",
    title: "Awards & Honors",
    icon: Trophy,
    category: "career",
    allowMultiple: true,
    defaultVisible: false,
    emptyState: "No awards listed yet.",
    editorComponent: "GenericListEditor",
    rendererComponent: "GenericListRenderer",
    layout: {
      preferredColumn: "main",
      priority: 25,
      collapsible: true,
      allowPageBreak: true,
      keepTogether: false,
    },
    capabilities: {
      aiRewrite: true,
      reorder: true,
      duplicate: true,
      deletable: true,
      hideable: true,
      exportable: true,
    },
    recommendedFor: ["all"],
    badges: ["Recommended"],
    fields: [
      { name: "title", label: "Award Title", type: "text", required: true, placeholder: "e.g. Employee of the Month" },
      { name: "issuer", label: "Issuer", type: "text", required: true, placeholder: "e.g. Google Cloud Division" },
      { name: "date", label: "Date Awarded", type: "text", required: false, placeholder: "e.g. Oct 2024" },
      { name: "description", label: "Description", type: "textarea", required: false, placeholder: "Recognized for driving key cloud migrations..." }
    ],
    defaultData: { title: "", issuer: "", date: "", description: "" }
  },
  {
    id: "achievements",
    title: "Achievements",
    icon: Star,
    category: "career",
    allowMultiple: true,
    defaultVisible: false,
    emptyState: "No achievements added yet.",
    editorComponent: "GenericListEditor",
    rendererComponent: "GenericListRenderer",
    layout: {
      preferredColumn: "main",
      priority: 20,
      collapsible: true,
      allowPageBreak: true,
      keepTogether: false,
    },
    capabilities: {
      aiRewrite: true,
      reorder: true,
      duplicate: true,
      deletable: true,
      hideable: true,
      exportable: true,
    },
    recommendedFor: ["marketing", "sales"],
    badges: [],
    fields: [
      { name: "title", label: "Achievement Summary", type: "text", required: true, placeholder: "e.g. Scaled API throughput by 400%" },
      { name: "description", label: "Context / Details", type: "textarea", required: false, placeholder: "Redesigned distributed Redis cluster to resolve bottleneck..." },
      { name: "date", label: "Date", type: "text", required: false, placeholder: "e.g. 2024" }
    ],
    defaultData: { title: "", description: "", date: "" }
  }
];
